import React, { useCallback, useMemo, useState, useRef } from "react";
import { deleteVal, getVal, setVal } from "oolib";
import TooltipV2 from "@/components/UI/Tooltip";
import { ButtonGhost } from "oolib/dist/v2/components/Buttons";

import { useMetricCardConfig } from "./ConfigForm_Context";
import { ConfigForm_SectionWrapper } from "./ConfigForm_SectionWrapper";
import { DataFieldInputSelector } from "@/components/DataFieldInputSelector";
import { SearchableLightbox } from "@/components/DataFieldInputSelector/SearchableLightbox";
import { getPickerProp } from "../../utils/pickerVariantConfig";
import { normalizeToArray } from "../index";
import { CollapseTransition, CollapseTransitionItem } from "../../utils/CollapseTransition";
import { AnimatePresence } from "framer-motion";
import { useVisibilityModifier } from "../../utils/useVisibilityModifier";
import {
  VALUE_SELECTION_KEY_OVERRIDES,
  VALUE_SELECTION_KEYS,
  writeValueSelectionMode,
  isNothingPicked,
} from "../utils/valueSelection";


// What a count counts: the documents in the dataset, or the annotations made on
// them. Only meaningful on a whole-dataset pick — a field statistic already
// names what it measures.
const COUNT_TYPE_OPTIONS = [
  { display: "Document Count", value: "document" },
  { display: "Annotation Count", value: "annotation" },
];

/** Module scope: a stable identity, so the modifier does not rebuild every render. */
const VALUE_SELECTION_LABELS = {
  allSelected: 'All values selected',
  noneSelected: 'Select options (required)',
};

export const ConfigForm_DataSection = () => {
  const {
    widgetConfig,
    updateWidget,
    dataKeys,
    measureMode,
    allFieldOptions,
    docCountValue,
    arithmeticState,
    handleDocCountSelect,
    handleClearData,
    fieldValueOptions,
    fieldValueCountsStatus,
  } = useMetricCardConfig();

  const [showAddFieldLightbox, setShowAddFieldLightbox] = useState(false);
  const addBtnRef = useRef(null);

  const selectedField = getVal(widgetConfig, dataKeys.field);
  const isNumberInput = selectedField?.comp === 'NumberInput';

  // --- Arithmetic state (shared with ConfigureSection via context) ---
  const {
    dataFields,
    sortedLetters,
    numberInputOptions,
    canAddFields,
    handleFieldAChange,
    handleAddField,
    handleFieldChange: handleArithFieldChange,
    handleRemoveField
  } = arithmeticState;

  // The raw stored mode, never readValueSelectionState's (it defaults to
  // "manual" when absent, which is the very thing we need to tell apart).
  const storedSelectionMode = getVal(widgetConfig, VALUE_SELECTION_KEYS.modeKey);

  // Build field A value with modifier (selected values) for FieldSlotCard
  const fieldAValue = useMemo(() => {
    const fieldA = dataFields.A;
    if (!fieldA) return null;
    if (fieldA.comp === 'NumberInput' || fieldValueOptions.length === 0) return fieldA;

    const stored = getVal(widgetConfig, dataKeys.selectedFieldValue);

    // No key stored means "every value" at runtime (the widget aggregates over
    // all of fieldValueCounts — see selectedOptionsData in ../index.js), so the
    // picker shows every option checked. So does a bare [] that the picker never
    // wrote: it predates the "nothing picked" meaning (see isNothingPicked).
    const modifierVal = !isNothingPicked(stored, storedSelectionMode) && normalizeToArray(stored).length === 0
      ? fieldValueOptions
      : normalizeToArray(stored)
          .map(v => fieldValueOptions.find(opt => opt.value === v))
          .filter(Boolean);

    return {
      ...fieldA,
      modifier: modifierVal,
    };
  }, [dataFields.A, fieldValueOptions, getVal(widgetConfig, dataKeys.selectedFieldValue), storedSelectionMode]);

  // --- Value selection (categorical field A): Show/Hide lightbox ---
  // The bar chart's eye control, driven by useVisibilityModifier like every
  // other widget. Only the manual WRITE differs: this card saves the SELECTED
  // ids to selectedFieldValue (absent = every value; [] = none, but only once a
  // manual mode is recorded — see isNothingPicked) because the selection is its
  // data definition, so it goes in through the hook's onManualChange seam.
  // Dynamic mode is the hook's own top/bottom N state.
  //
  // Reading an unwritten [] as "none" here is what made the card blank on a mere
  // tab switch: the picker showed every box unticked, and Dynamic -> Manual
  // stamps the mode (writeMode) without touching the list, so the card's guard
  // then fired on a selection the user never made.
  const storedSelection = getVal(widgetConfig, dataKeys.selectedFieldValue);
  const selectedIdSet = useMemo(() => {
    const picked = normalizeToArray(storedSelection);
    if (picked.length === 0 && !isNothingPicked(storedSelection, storedSelectionMode)) {
      return new Set(fieldValueOptions.map(o => String(o.value)));
    }
    return new Set(picked.map(String));
  }, [storedSelection, storedSelectionMode, fieldValueOptions]);

  const hiddenValues = useMemo(
    () => fieldValueOptions.filter(o => !selectedIdSet.has(String(o.value))).map(o => o.value),
    [fieldValueOptions, selectedIdSet]
  );

  /** Persist a manual selection: ids, stat-function gating and the auto title. */
  const applyManualSelection = useCallback((selectedItems) => {
    updateWidget(w => {
      const selectedValues = selectedItems.map(item => item.value);

      // "Every value" has exactly one representation: the absent key. An explicit
      // list of today's values is a snapshot — a value added to the field later
      // would be silently excluded while the picker still reads "all values
      // selected" — and on a high-cardinality field it bloats the config and
      // joins every name into the auto title.
      const isAllSelected =
        fieldValueOptions.length > 0 && selectedValues.length === fieldValueOptions.length;

      w = isAllSelected
        ? deleteVal(w, dataKeys.selectedFieldValue)
        : setVal(w, dataKeys.selectedFieldValue, selectedValues);
      w = writeValueSelectionMode(w, 'manual');

      if (selectedValues.length <= 1) {
        w = deleteVal(w, dataKeys.categoricalStatFunction);
      }

      // Auto-generate title. "All values" falls back to the plain field label,
      // which is what the card renders when no title is stored.
      const field = getVal(w, dataKeys.field);
      if (field && !isAllSelected && selectedItems.length > 0) {
        const valueNames = selectedItems.map(item => item.displayName || item.display).join(', ');
        w = setVal(w, dataKeys.field1, `${field.display} - ${valueNames}`);
      } else {
        w = deleteVal(w, dataKeys.field1);
      }
      return w;
    });
  }, [updateWidget, dataKeys, fieldValueOptions.length]);

  // Writing the MODE is what gives a stored [] its new "nothing picked" meaning,
  // so a config that predates the picker must be normalised in the same write:
  // its [] meant "every value", which is spelled as the ABSENT key now. Without
  // this, merely switching Dynamic -> Manual (writeMode, which never touches the
  // list) would silently blank a card the user never edited.
  //
  // Only the mode / dynamic writes come through here — the genuine "I unticked
  // everything" path goes via onManualChange (applyManualSelection) and keeps
  // writing [] with a mode, exactly as intended.
  const updateWidgetNormalisingLegacyEmpty = useCallback((updaterFn) => {
    updateWidget((w) => {
      // Read BEFORE the updater runs: oolib's setVal mutates in place and hands
      // back the same object, so asking `w` afterwards would return the mode the
      // updater just wrote and this would never fire.
      const hadMode = getVal(w, VALUE_SELECTION_KEYS.modeKey);
      const next = updaterFn(w);
      const selection = getVal(next, dataKeys.selectedFieldValue);
      return !hadMode && Array.isArray(selection) && selection.length === 0
        ? deleteVal(next, dataKeys.selectedFieldValue)
        : next;
    });
  }, [updateWidget, dataKeys]);

  const valueSelectionModifier = useVisibilityModifier({
    dimension: 1,
    widgetConfig,
    dataKeys,
    data: fieldValueOptions,
    isNumberInput,
    updateConfig: updateWidgetNormalisingLegacyEmpty,
    keyOverrides: VALUE_SELECTION_KEY_OVERRIDES,
    optionsOverride: fieldValueOptions,
    manualHiddenOverride: hiddenValues,
    onManualChange: applyManualSelection,
    labelOverrides: VALUE_SELECTION_LABELS,
    icon: 'Cursor',
    tooltipText: 'Choose values to include',
    loading: fieldValueCountsStatus === 'loading',
    renderWhenEmpty: true,
  });

  // --- "+" add field button (in section header, for arithmetic expressions) ---
  const addFieldButton = measureMode === 'fieldStat' ? (
    <TooltipV2
      text={
        !dataFields.A
          ? "Add data to visualize"
          : canAddFields
            ? "Add additional number fields to include in this calculation"
            : "Activates only on number fields for arithmetic aggregation"
      }
      placement="top"
    >
      <span ref={addBtnRef}>
        <ButtonGhost
          icon="Plus"
          onClick={() => setShowAddFieldLightbox(true)}
          disabled={!canAddFields}
          style={{ padding: "4px" }}
        />
      </span>
    </TooltipV2>
  ) : null;

  const isDocCount = measureMode === 'docCount';

  const countTypeValue = useMemo(() => {
    const value = getVal(widgetConfig, dataKeys.countType) || "document";
    return {
      ...COUNT_TYPE_OPTIONS.find(opt => opt.value === value),
      dataType: "Count type",
    };
  }, [widgetConfig, dataKeys.countType]);

  return (
    <ConfigForm_SectionWrapper icon="Database" title="Data" headerRight={addFieldButton}>

      {/* One picker for both measure modes. Every dataset folder in the cascade
          splits into two click targets: the row BODY picks the dataset (count its
          documents) and the chevron drills into its fields (field statistic). */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {/* Field rows — Field A gets allFieldOptions (grouped by content type),
              Fields B, C, D... get numberInputOptions (NumberInput only). */}
          <AnimatePresence initial={false}>
            {sortedLetters.map((letter) => {
              const fieldRow = (
                <DataFieldInputSelector
                  key={letter}
                  indicator={sortedLetters.length > 1 ? { label: letter } : undefined}
                  options={letter === 'A' ? allFieldOptions : numberInputOptions}
                  value={letter === 'A' ? (docCountValue || fieldAValue) : dataFields[letter]}
                  onChange={(id, v) => {
                    if (letter !== 'A') {
                      handleArithFieldChange(letter, v);
                      return;
                    }

                    // A dataset folder body was picked → count that dataset's docs.
                    if (v?.__docCount) {
                      handleDocCountSelect(v);
                      return;
                    }

                    const { modifier: modVal, ...fieldOnly } = v || {};
                    const cleanField = Object.keys(fieldOnly).length > 0 ? fieldOnly : null;

                    // Check if the field itself changed (not just a modifier toggle)
                    // Leaving docCount for a field is always a real field change,
                    // even if the previously stored field happened to match.
                    const fieldChanged = isDocCount || dataFields.A?.value !== cleanField?.value;

                    if (fieldChanged) {
                      handleFieldAChange(cleanField);
                      return;
                    }

                    // Only modifier toggled — update selectedFieldValue directly
                    if (modVal) applyManualSelection(modVal);
                  }}
                  actions={letter === 'A'
                    ? ((isDocCount || dataFields.A) ? [{ icon: 'X', onClick: () => {
                        // docCount, or the only field: clear the slot entirely.
                        // With B, C, D... present, just null out A and keep the rest.
                        if (isDocCount) {
                          handleClearData();
                        } else if (sortedLetters.length === 1) {
                          handleFieldAChange(null);
                        } else {
                          handleArithFieldChange('A', null);
                        }
                      }, hoverColor: 'red' }] : [])
                    : [{ icon: 'X', onClick: () => handleRemoveField(letter), hoverColor: 'red' }]
                  }
                  placeholder="Select field"
                  picker={getPickerProp({
                    widgetComp: 'WidgetMetricCard',
                    slot: letter === 'A' ? 'field' : 'numberFields',
                  })}
                  {...(letter === 'A' && !isDocCount && !isNumberInput ? { modifier: valueSelectionModifier } : {})}
                />
              );

              // Field A renders without animation; B, C, D... animate in/out
              if (letter === 'A') return fieldRow;

              return (
                <CollapseTransitionItem key={letter} id={`field-${letter}`}>
                  {fieldRow}
                </CollapseTransitionItem>
              );
            })}
          </AnimatePresence>
      </div>

      <CollapseTransition isVisible={isDocCount}>
        <DataFieldInputSelector
          id={dataKeys.countType}
          options={COUNT_TYPE_OPTIONS}
          value={countTypeValue}
          onChange={(id, v) => {
            updateWidget(w => setVal(w, dataKeys.countType, v.value));
          }}
          placeholder="Select count type"
          placeholderIcon="Hash"
          trailingIcon="CaretRight"
          tooltip={{ text: "Choose what gets counted", placement: "top" }}
          hideSearchHeader
        />
      </CollapseTransition>

      {showAddFieldLightbox && (
        <SearchableLightbox
          anchorRef={addBtnRef}
          onClose={() => setShowAddFieldLightbox(false)}
          options={numberInputOptions}
          grouped={numberInputOptions.some(item => item.options && Array.isArray(item.options))}
          value={undefined}
          onChange={(_, selectedField) => {
            let enriched = selectedField;
            if (selectedField && !selectedField.categoryLabel) {
              const group = numberInputOptions.find(g =>
                Array.isArray(g.options) && g.options.some(o => o.value === selectedField.value)
              );
              if (group) enriched = { ...selectedField, categoryLabel: group.display };
            }
            handleAddField(enriched);
            setShowAddFieldLightbox(false);
          }}
          searchPlaceholder="Search fields"
        />
      )}

    </ConfigForm_SectionWrapper>
  );
};
